import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { number, string, oneOfType } from 'prop-types';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { normalizeNumberField } from '#component/shipment/form/cargo/helper';
import {
  DEFAULT_DISTANCE_DEVIATION,
  getDistanceDeviationOptions
} from '#component/shipment/form/delivery/distance-deviation';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import RHFLocationMap from '#component/form/rhf/rhf-location-map';
import SwitchView from '#component/general/switch-view';
import RHFAutocompleteServiceField from '#component/general/autocomplete-service-field/rhf-autocomplete-service-field';
import { renderAutocompleteOption } from '#component/general/autocomplete-service-field';
import { useGoogleOptionsService } from '#service/autocomplete-service/hooks';
import {
  LocationFieldContainer,
  FullSizeLocationDropdown,
  CenteredAddress,
  DeviationInputs,
  ButtonGroup
} from './styles';
import { formattedAddress, convertDistance } from '#helper';

const addressFields = [
  { id: 1, name: 'address.address1', label: 'Address 1' },
  { id: 2, name: 'address.address2', label: 'Address 2' },
  { id: 3, name: 'address.city', label: 'City' },
  { id: 4, name: 'address.state', label: 'State' },
  { id: 5, name: 'address.country', label: 'Country' },
  { id: 6, name: 'address.zipCode', label: 'Zip code' }
];

/**
 * Renders address block with autocomplete field.
 * @returns {React.FC}
 */

const AddressLocationFields = props => {
  const { id } = props;
  const [tab, setTab] = useState(0);

  const { watch, setValue, formState } = useFormContext() || {};
  const radiusFieldValue = watch('radius');
  const locationsFieldValue = watch('locations');

  /*
    Structured address fields live on a hidden tab (tab 1). When a Save attempt
    produces address errors, switch to that tab so the field-level messages are visible.
  */
  const submitCount = formState?.submitCount;
  const addressErrors = formState?.errors?.address;
  useEffect(() => {
    if (submitCount > 0 && addressErrors && Object.keys(addressErrors).length) {
      setTab(1);
    }
  }, [submitCount, addressErrors]);

  const measurementSystem = useSelector(s =>
    s.account && s.account.measurementSystem, shallowEqual);
  const radiusValue = radiusFieldValue ?
    Number(radiusFieldValue) : convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem);
  const metricRadiusValue = convertDistance(radiusValue, measurementSystem, 'metric');
  const hasLocation = Array.isArray(locationsFieldValue) ?
    locationsFieldValue.length > 0 :
    !!locationsFieldValue;
  const radiusOptions = useMemo(
    () => getDistanceDeviationOptions(radiusValue, measurementSystem),
    [radiusValue, measurementSystem]
  );

  const changeTab = newTab => () => setTab(newTab);

  const handleMapFieldChange = (field, value) => {
    if (!setValue) {
      return;
    }
    setValue(field, value, { shouldDirty: true });
  };

  const updateFormFields = optionDetails => {
    if (setValue) {
      setValue('formattedAddress', formattedAddress(optionDetails.address), { shouldDirty: true });
      setValue('locations', optionDetails.locations, { shouldDirty: true });
      setValue('address', optionDetails.address, { shouldDirty: true });
      setValue(
        'radius',
        optionDetails.radius ?
          convertDistance(optionDetails.radius, 'metric', measurementSystem) :
          convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem),
        { shouldDirty: true }
      );
    }
  };

  return (
    <LocationFieldContainer>
      <FullSizeLocationDropdown tab={tab}>
        <RHFAutocompleteServiceField
          name="formattedAddress"
          onOptionChange={updateFormFields}
          inputProps={{ label: 'Location', variant: 'outlined' }}
          useOptionsService={useGoogleOptionsService}
          renderOption={renderAutocompleteOption}
        />
        <RHFLocationMap
          name="locations"
          id={id}
          radius={metricRadiusValue}
          measurementSystem={measurementSystem}
          change={handleMapFieldChange}
        />
        {hasLocation &&
          <DeviationInputs>
            <RHFTextField
              name="radius"
              label="Distance deviation"
              variant="outlined"
              select
            >
              {radiusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFTextField>
            <RHFTextField
              name="minutes"
              label="Time deviation"
              variant="outlined"
              onChange={e => {
                const val = normalizeNumberField(e.target.value);
                if (setValue) setValue('minutes', val, { shouldDirty: true });
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">min.</InputAdornment>
              }}
            />
          </DeviationInputs>}
      </FullSizeLocationDropdown>
      <CenteredAddress tab={tab}>
        {addressFields.map(field => {
          const prop = {};
          if (field.name === 'address.address1') {
            return (
              <RHFAutocompleteServiceField
                key={field.id}
                name={field.name}
                onOptionChange={updateFormFields}
                inputProps={{ label: field.label, variant: 'outlined' }}
                useOptionsService={useGoogleOptionsService}
                renderOption={renderAutocompleteOption}
              />
            );
          }
          return <RHFTextField
            key={field.id}
            name={field.name}
            label={field.label}
            variant="outlined"
            {...prop}
          />;
        })}
      </CenteredAddress>
      <ButtonGroup>
        <SwitchView tab={tab} changeTab={changeTab} />
      </ButtonGroup>
    </LocationFieldContainer>
  );
};

AddressLocationFields.propTypes = {
  id: oneOfType([
    number,
    string
  ]).isRequired
};

export default AddressLocationFields;
